import React from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  useTheme,
} from '@mui/material';
import type { QuestionInputProps } from '../types/questionnaire';

/**
 * Shared select input component for questionnaires
 * Handles: question_singleChoice, question_multipleChoice, question_choice
 */
export const SelectInput: React.FC<QuestionInputProps> = ({
  question,
  value,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const { answerOptions, numberOfChoices } = question;

  if (!answerOptions || answerOptions.length === 0) {
    return null;
  }

  // Derive whether this is multiple choice from number_of_choices
  const isMultiple =
    numberOfChoices === null || (numberOfChoices && numberOfChoices > 1);

  const handleMultiple = (optionId: string) => {
    const currentValue = Array.isArray(value) ? value : [];
    let newValue = [];
    if (currentValue.includes(optionId)) {
      newValue = currentValue.filter((id) => id !== optionId);
    } else {
      if (numberOfChoices && currentValue.length >= numberOfChoices) {
        newValue = currentValue;
      } else {
        newValue = [...currentValue, optionId];
      }
    }
    onChange(newValue.sort());
  };

  const handleSingle = (optionId: string) => {
    onChange([optionId]);
  };

  // Multiple choice or single choice with number_of_choices > 1
  if (isMultiple) {
    return (
      <FormGroup
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {answerOptions.map((item, idx) => {
          const currentValue = Array.isArray(value) ? value : [];
          const isChecked = currentValue.includes(item.id);
          return (
            <FormControlLabel
              key={idx}
              onChange={!disabled ? () => handleMultiple(item.id) : undefined}
              style={{
                backgroundColor: 'white',
                borderRadius: 6,
                border: `1px solid ${theme.palette.colors.gray200}`,
                margin: 0,
              }}
              value={item.id}
              control={
                <Checkbox
                  checked={isChecked}
                  value={item.id}
                  disabled={disabled}
                />
              }
              label={item.coding?.display}
            />
          );
        })}
      </FormGroup>
    );
  }

  // Single choice
  const currentValue = Array.isArray(value) ? value[0] : value;
  return (
    <RadioGroup
      value={currentValue || ''}
      onChange={!disabled ? (e) => handleSingle(e.target.value) : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {answerOptions.map((item, idx) => {
        return (
          <FormControlLabel
            key={idx}
            style={{
              backgroundColor: 'white',
              borderRadius: 6,
              border: `1px solid ${theme.palette.colors.gray200}`,
              margin: 0,
            }}
            value={item.id}
            control={
              <Radio checked={currentValue === item.id} disabled={disabled} />
            }
            label={item.coding?.display}
          />
        );
      })}
    </RadioGroup>
  );
};
